// File: src/pages/CustomerGroupList.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Spinner,
  Badge,
} from "reactstrap";
import { FaPlus, FaPencilAlt } from "react-icons/fa";
import { Trash2 } from "react-feather";
import swal from "sweetalert";
import * as XLSX from "xlsx";

// ✅ real endpoints (paths) from your src/import
import {
  View_CustomerGroup,
  Delete_CustomerGroup_by_id,
  PurchaseProductList_Product,
  Product_Price_Bulk_Update,
} from "../import/ApiEndPoint/Api";

// ✅ your call helpers
import { _Get, _Delete } from "../import/ApiEndPoint/ApiCalling";

// (optional) axios instance (you already use it for bulk product updates in other pages)
import axiosConfig from "../import/axiosConfig";
import { useNavigate } from "react-router-dom";

const injectOnce = () => {
  if (document.getElementById("cg-inline-css")) return;
  const s = document.createElement("style");
  s.id = "cg-inline-css";
  s.textContent = `
  .cg-card{ border-radius:14px; border:1px solid #e5e7eb; }
  /* ↓ reduce outer padding on the card body (shrinks white space around the table) */
  .cg-card .card-body{ padding: 0px; }

  .cg-toolbar{ padding:8px; background:#f4f7f6; border-radius:14px; border:1px solid #e5e7eb; }
  .cg-toolbar .count-pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }

  .cg-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
  .cg-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:900px; }
  .cg-table thead th{ background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em; border-bottom:1px solid #e5e7eb; padding:8px; text-align:center; white-space:nowrap; }
  .cg-table tbody td{ padding:8px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center; }

  .cg-table tbody tr:hover{ background:#f9fbff; }

  /* ↓ keep action buttons on ONE line, always */
  .cg-actions{ display:flex; justify-content:center; align-items:center; gap:8px; flex-wrap:nowrap; white-space:nowrap; }

  /* ↓ a fixed width for the ACTIONS column so icons don’t wrap */
  .cg-col-actions{ width:120px; }

  /* ↓ slightly smaller chips to save width */
  .cg-chip-edit, .cg-chip-del{ display:inline-grid; place-items:center; width:26px; height:26px; border-radius:16px; cursor:pointer; }
  .cg-chip-edit{ background:#d46f10; }
  .cg-chip-del{ background:#ec1809; }

  .cg-note{ color:#b91c1c; font-weight:700; margin:8px 0; }
`;
  document.head.appendChild(s);
};

const toStr = (v) => (v === null || v === undefined ? "" : String(v));

export default function CustomerGroupList() {
  useEffect(() => injectOnce(), []);

  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [updatingProducts, setUpdatingProducts] = useState(false);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");

  const user = useMemo(
    () => JSON.parse(localStorage.getItem("userData") || "{}"),
    []
  );

  // ===== Load groups =====
  const loadGroups = async () => {
    if (!user?.database) {
      setLoading(false);
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const res = await _Get(View_CustomerGroup, user.database);
      const list = Array.isArray(res?.CustomerGroup) ? res.CustomerGroup : [];
      const active = list.filter((g) => g?.status === "Active").reverse();
      setRows(active);

      // save current max discount (used to decide bulk-recalc necessity)
      const max =
        active.length > 0
          ? active.reduce(
              (m, o) => (Number(o?.discount) > m ? Number(o?.discount) : m),
              0
            )
          : 0;
      localStorage.setItem("MaxGst", String(max));
    } catch (err) {
      console.error(err);
      setRows([]);
      swal("Error", "Unable to fetch Customer Groups", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        toStr(r?.groupName).toLowerCase().includes(q) ||
        toStr(r?.id).toLowerCase().includes(q) ||
        toStr(r?.grade).toLowerCase().includes(q) ||
        toStr(r?.status).toLowerCase().includes(q) ||
        toStr(r?.discount).toLowerCase().includes(q)
    );
  }, [rows, search]);

  // ===== Recalculate Product Prices (when top discount changes) =====
  const recalcAllProducts = async () => {
    setUpdatingProducts(true);
    try {
      // fetch current groups → compute max discount again
      const resGroups = await _Get(View_CustomerGroup, user?.database);
      const active = Array.isArray(resGroups?.CustomerGroup)
        ? resGroups.CustomerGroup.filter((g) => g?.status === "Active")
        : [];
      const maxDiscount =
        active.length > 0
          ? active.reduce(
              (m, o) => (Number(o?.discount) > m ? Number(o?.discount) : m),
              0
            )
          : 0;
      localStorage.setItem("MaxGst", String(maxDiscount));

      // fetch products
      const resProd = await _Get(PurchaseProductList_Product, user?.database);
      const products = Array.isArray(resProd?.Product) ? resProd.Product : [];

      // recalc each product
      const mapped = products.map((p) => {
        const purchase = Number(p?.Purchase_Rate || 0);
        const gstRate = Number(p?.GSTRate || 0);
        const minProfit =
          Number(p?.ProfitPercentage || 0) > 3
            ? Number(p?.ProfitPercentage)
            : 3;
        const profitFactor = (100 + minProfit) / 100;
        const gstFactor = (100 + gstRate) / 100;
        const discFactor = (100 + Number(maxDiscount || 0)) / 100;

        const SalesRate = Number((purchase * profitFactor).toFixed(2));
        const Product_MRP = Number(
          (SalesRate * gstFactor * discFactor).toFixed(2)
        );

        return {
          id: p?._id,
          Product_MRP,
          SalesRate,
          ProfitPercentage: p?.ProfitPercentage || minProfit,
          maxDiscount: Number(maxDiscount || 0),
        };
      });

      // bulk update
      await axiosConfig.put(Product_Price_Bulk_Update, { Products: mapped });
      swal(
        "Success",
        "Product prices updated with new discount baseline.",
        "success"
      );
    } catch (err) {
      console.error(err);
      swal("Error", "Bulk price update failed", "error");
    } finally {
      setUpdatingProducts(false);
    }
  };

  // ===== Delete group =====
  const onDelete = (row) => {
    swal("Warning", "Sure you want to delete this group?", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    }).then(async (v) => {
      if (v !== "delete") return;
      try {
        const prevMax = Number(localStorage.getItem("MaxGst") || 0);
        await _Delete(Delete_CustomerGroup_by_id, row?._id);
        // reload groups first
        await loadGroups();

        // if we removed the previous max, recalc products
        if (Number(row?.discount || 0) === prevMax) {
          await recalcAllProducts();
        }
      } catch (err) {
        console.error(err);
        swal("Error", "Failed to delete the group", "error");
      }
    });
  };

  // ===== Export XLSX =====
  const exportXLSX = () => {
    const data = filtered.map((r, i) => ({
      "S.NO": i + 1,
      "GROUP ID": toStr(r?.id),
      "GROUP NAME": toStr(r?.groupName),
      GRADE: toStr(r?.grade),
      DISCOUNT: toStr(r?.discount),
      STATUS: toStr(r?.status),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "CustomerGroups");
    XLSX.writeFile(wb, "CustomerGroupList.xlsx");
  };

  const countInfo = `${filtered.length}/${rows.length}`;

  return (
    <>
      {/* Toolbar */}
      <Row style={{ marginBottom: 10 }}>
        <Col sm="12">
          <div className="cg-toolbar">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <div style={{ fontWeight: 800, textTransform: "uppercase" }}>
                Customer Groups <span className="count-pill">{countInfo}</span>
              </div>

              <div style={{ flex: "1 1 240px" }} />

              <div style={{ minWidth: 260, display: "flex", gap: 8 }}>
                <Input
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <Button outline color="secondary" onClick={loadGroups}>
                  Refresh
                </Button>
                <Button
                  style={{
                    backgroundColor: "rgb(8, 91, 245)",
                    color: "white",
                    fontWeight: 600,
                    height: 32,
                    fontSize: 14,
                    textTransform: "uppercase",
                  }}
                  onClick={() => navigate("/app/grades/create/0")}
                >
                  <FaPlus size={10} /> Group
                </Button>
                <Button
                  style={{
                    backgroundColor: "rgb(8, 91, 245)",
                    color: "white",
                    fontWeight: 600,
                    height: 32,
                    fontSize: 14,
                    textTransform: "uppercase",
                  }}
                  onClick={exportXLSX}
                >
                  EXPORT .XSLX
                </Button>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Optional note */}
      <Row>
        <Col sm="12">
          <div className="cg-note">
            Note: If you delete/update the highest discount group, product rates
            will be recalculated. Please allow a brief moment for updates to
            finish.
          </div>
        </Col>
      </Row>

      {/* Body */}
      <Row>
        <Col sm="12">
          <Card className="cg-card">
            <CardBody>
              {loading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    margin: "4rem 0",
                  }}
                >
                  <Spinner style={{ height: "3rem", width: "3rem" }} />
                </div>
              ) : (
                <div className="cg-table-wrap">
                  <table className="cg-table">
                    <thead>
                      <tr>
                        <th className="cg-col-actions">ACTIONS</th>
                        <th className="cg-col-actions">STATUS</th>
                        <th className="cg-col-actions">GROUP ID</th>
                        <th className="cg-col-actions">GROUP NAME</th>
                        <th className="cg-col-actions">GRADE</th>
                        <th className="cg-col-actions">DISCOUNT (%)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            style={{ padding: 18, color: "#6b7280" }}
                          >
                            No groups found.
                          </td>
                        </tr>
                      ) : (
                        filtered.map((r, i) => (
                          <tr key={r?._id || i}>
                            <td className="cg-col-actions">
                              <div className="cg-actions">
                                <span
                                  className="cg-chip-edit"
                                  title="Edit"
                                  onClick={() =>
                                    navigate(`/app/grades/create/${r?._id}`)
                                  }
                                >
                                  <FaPencilAlt size={14} color="#fff" />
                                </span>
                                <span
                                  className="cg-chip-del"
                                  title="Delete"
                                  onClick={() => onDelete(r)}
                                >
                                  <Trash2 size={14} color="#fff" />
                                </span>
                              </div>
                            </td>
                            <td>
                              <Badge
                                color={
                                  toStr(r?.status).toLowerCase() === "active"
                                    ? "success"
                                    : "warning"
                                }
                              >
                                {r?.status || "Active"}
                              </Badge>
                            </td>
                            <td>{r?.id || "-"}</td>
                            <td style={{ textAlign: "left" }}>
                              {r?.groupName || "-"}
                            </td>
                            <td>{r?.grade || "-"}</td>
                            <td>{r?.discount ?? "-"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {updatingProducts ? (
                <div style={{ marginTop: 10 }}>
                  <Badge color="danger">
                    Updating product data, please wait…
                  </Badge>
                </div>
              ) : null}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
}
